import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  deleteGroup,
  deleteGroupUserPortal,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import { useNavigate } from 'react-router-dom';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { pagesConfig } from '../../../shared/pages-config';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface DeleteGroupDialogProps {
  open: boolean;
  handleClose: () => void;
  groupEmail: string;
  changeLogRoles: string[];
  userView: boolean;
}

export default function DeleteGroupDialog({
  open,
  handleClose,
  groupEmail,
  changeLogRoles,
  userView,
}: DeleteGroupDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteGroup(groupEmail, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Group deleted successfully!',
      });

      queryClient.invalidateQueries({ queryKey: [UserKeys.Groups] });
      queryClient.invalidateQueries({ queryKey: [UserKeys.UserGroups] });
      setChangeLogMessage('');
      navigate(`/${pagesConfig.groups.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting this group. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_details.delete_group, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: () => deleteGroupUserPortal(groupEmail),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Group deleted successfully!',
        });

        queryClient.invalidateQueries({ queryKey: [UserKeys.Groups] });
        queryClient.invalidateQueries({ queryKey: [UserKeys.UserGroups] });

        setChangeLogMessage('');
        navigate('/all-groups');
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue deleting this group. Please refresh and try again.',
        });
      },
      onSettled: () => {
        createEvent(analytics.userView.group_details.delete_group, {
          module: ModuleList.modules.userView.name,
          section: ModuleList.modules.userView.sections.groupDetails,
        });
      },
    });

  function handleSubmit() {
    if (
      !userView &&
      isAbleToSeeUIElement(changeLogRoles, DirectoryRoles.DeleteGroup.value)
    ) {
      setOpenChangeLogDialog(true);
    } else if (userView) {
      userPortalMutate();
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Delete Group`}
      dialogContent={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            flexDirection: 'column',
          }}
        >
          <Typography>
            {`Do you wish to delete the group ${groupEmail}?`}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete',
        action: handleSubmit,
        loading: isPending || userPortalPending,
        disabled: isPending || userPortalPending,
        color: 'error',
      }}
    />
  );
}
